import React, { useEffect, useRef } from 'react';
import * as S from './styles';
import { usePageMetadata } from '../../utils/usePageMetadata';
import { projects } from '../../data/projects';

export const Projects: React.FC = () => {
  const projectRefs = useRef<(HTMLElement | null)[]>([]);

  usePageMetadata({
    title: 'Projects | Austin Davenport',
    description: 'Curated substantial projects Austin Davenport has built, including YouTool.io, ClipboardIt, Smooth It, Notion templates, and creator tools.',
    path: '/projects',
  });

  // Set up scroll observers after projects load
  useEffect(() => {
    if (projects.length > 0 && projectRefs.current.length > 0) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              entry.target.classList.add('visible');
            }
          });
        },
        { threshold: 0.1, rootMargin: '0px 0px -10% 0px' }
      );

      projectRefs.current.forEach(ref => {
        if (ref) observer.observe(ref);
      });

      return () => observer.disconnect();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projects, projectRefs.current]);

  return (
    <S.Container>
      <S.Header>
        <S.Title>Projects</S.Title>
        <S.Description>
          Small tools, products, and creative experiments I've built for content creators.
        </S.Description>
      </S.Header>

      <S.ProjectsGrid>
        {projects.map((project, index) => (
          <S.ProjectCard
            key={project.id}
            ref={el => projectRefs.current[index] = el}
            accentColor={project.color_accent}
            className="project-tile"
            href={project.project_url}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              animationDelay: `${index * 0.1}s`
            }}
          >
            <S.ProjectImageWrapper>
              <S.ProjectImage
                style={{ backgroundImage: `url(${project.image_url})` }}
              />
              <S.ColorOverlay color={project.color_accent} />
              {project.featured && (
                <S.FeaturedBadge>Featured</S.FeaturedBadge>
              )}
            </S.ProjectImageWrapper>

            <S.ProjectContent>
              <S.CategoryTag color={project.color_accent}>
                {project.category}
              </S.CategoryTag>
              <S.ProjectTitle>{project.title}</S.ProjectTitle>
              <S.ProjectDescription>{project.description}</S.ProjectDescription>
              <S.ExploreButton color={project.color_accent}>
                <S.ExploreDesktopText>Explore Project</S.ExploreDesktopText>
                <S.ExploreMobileText>Open</S.ExploreMobileText>
                <i className='bx bx-right-arrow-alt'></i>
              </S.ExploreButton>
            </S.ProjectContent>
          </S.ProjectCard>
        ))}
      </S.ProjectsGrid>
    </S.Container>
  );
};

export default Projects;
